import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import QuizAnswerMode from './QuizAnswerMode';
import QuizReviewMode from './QuizReviewMode';
import QuizPreviewMode from './QuizPreviewMode';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import type { Question, Paper, QuizProps } from './types';

export { type QuizProps } from './types';

export default function Quiz(props?: QuizProps) {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 模式判断
  const mode = props?.mode || 'answer';
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const isEditorPreview = searchParams.get('mode') === 'editor-preview' || location.state?.previewData?.isEditorPreview;
  const fromResult = location.state?.fromResult;
  const isProxyView = location.state?.isProxyView;
  const studentName = location.state?.studentName;
  const isPreview = isPreviewMode || isEditorPreview;

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [examQuestions, setExamQuestions] = useState<Question[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [reviewData, setReviewData] = useState<{
    answers: Record<string, string>;
    score: number;
    timeLeft: number;
    objectiveScore?: number | null;
    objectiveTotalScore?: number | null;
    programmingScore?: number | null;
    questionFeedbacks?: Record<string, { score?: number; feedback?: string }> | null;
  } | null>(null);

  // 初始化数据加载
  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // 预览模式：直接加载数据
      if (isPreviewMode || isEditorPreview) {
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 代查模式（管理员/教师查看学员答题记录）
      if (isProxyView) {
        // 获取当前登录用户（教师）信息
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });
        }

        const resultData = location.state?.resultData;
        if (resultData && resultData.questions && resultData.questions.length > 0) {
          // 从数据库补充布局/缩放字段
          const { data: questionsData } = await supabase
            .from('questions')
            .select('id, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
            .eq('paper_id', resultData.paperId);

          let enrichedQuestions = resultData.questions;
          if (questionsData) {
            const questionMap = new Map(questionsData.map((q: any) => [q.id, q]));
            enrichedQuestions = resultData.questions.map((q: Question) => {
              const dbQ = questionMap.get(q.id);
              if (dbQ) {
                const optionImageWidths = dbQ.option_image_widths || {};
                const mappedOptions = (q.options || []).map((opt: any) => ({
                  ...opt,
                  imageWidth: optionImageWidths[opt.id] || opt.imageWidth || 100
                }));
                return {
                  ...q,
                  titleImageLayout: dbQ.title_image_layout || (q as any).titleImageLayout || 'auto',
                  titleImageWidths: dbQ.title_image_widths || q.titleImageWidths || [],
                  titleImages: dbQ.title_images || q.titleImages || [],
                  imagePosition: dbQ.image_position || (q as any).imagePosition || 'below',
                  optionLayout: dbQ.option_layout || (q as any).optionLayout || 'grid',
                  options: mappedOptions
                };
              }
              return q;
            });
          }

          setPaper({
            id: resultData.paperId,
            title: resultData.paperTitle,
            questions: enrichedQuestions,
            total_score: resultData.totalScore,
            category_type: ''
          });
          setExamQuestions(enrichedQuestions);
          setReviewData({
            answers: resultData.answers || {},
            score: resultData.score,
            timeLeft: resultData.timeLeft,
            objectiveScore: resultData.objectiveScore,
            objectiveTotalScore: resultData.objectiveTotalScore,
            programmingScore: resultData.programmingScore,
            questionFeedbacks: resultData.questionFeedbacks || null
          });
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 回顾模式（从答题结果页跳转）
      if (fromResult) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // 从 Edge Function 获取最新答题记录数据
          const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ action: 'get_records' }),
          });

          const result = await response.json();

          if (result.success && result.records && result.records.length > 0) {
            const currentRecord = result.records.find((r: any) => r.paper_id === paperId);

            if (currentRecord && currentRecord.questions) {
              // 从数据库补充布局/缩放字段
              const { data: questionsData } = await supabase
                .from('questions')
                .select('id, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                .eq('paper_id', paperId);

              let enrichedQuestions = currentRecord.questions;
              if (questionsData) {
                const questionMap = new Map(questionsData.map((q: any) => [q.id, q]));
                enrichedQuestions = currentRecord.questions.map((q: Question) => {
                  const dbQ = questionMap.get(q.id);
                  if (dbQ) {
                    const optionImageWidths = dbQ.option_image_widths || {};
                    const mappedOptions = (q.options || []).map((opt: any) => ({
                      ...opt,
                      imageWidth: optionImageWidths[opt.id] || opt.imageWidth || 100
                    }));
                    return {
                      ...q,
                      titleImageLayout: dbQ.title_image_layout || (q as any).titleImageLayout || 'auto',
                      titleImageWidths: dbQ.title_image_widths || q.titleImageWidths || [],
                      titleImages: dbQ.title_images || q.titleImages || [],
                      imagePosition: dbQ.image_position || (q as any).imagePosition || 'below',
                      optionLayout: dbQ.option_layout || (q as any).optionLayout || 'grid',
                      options: mappedOptions
                    };
                  }
                  return q;
                });
              }

              setPaper({
                id: currentRecord.paper_id,
                title: currentRecord.paper_title,
                questions: enrichedQuestions,
                total_score: currentRecord.total_score,
                category_type: ''
              });
              setExamQuestions(enrichedQuestions);
              setReviewData({
                answers: currentRecord.answers || {},
                score: currentRecord.score,
                timeLeft: currentRecord.time_left,
                objectiveScore: currentRecord.objectiveScore ?? currentRecord.objective_score,
                objectiveTotalScore: currentRecord.objectiveTotalScore ?? currentRecord.objective_total_score,
                programmingScore: currentRecord.programmingScore ?? currentRecord.programming_score,
                questionFeedbacks: currentRecord.question_feedbacks || null
              });
              setHasPermission(true);
              setLoading(false);
              return;
            }
          }
        }
      }

      // 收藏练习模式已改为页面内切换，不再使用独立路由
      // 如果有人通过旧链接访问，重定向到收藏页面
      if (paperId === 'favorites-practice') {
        navigate('/favorites', { replace: true });
        return;
      }

      // 正常答题模式：检查登录和权限
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      setCurrentUser({ id: session.user.id, ...profile });

      // 加载试卷数据
      if (paperId) {
        const { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
        if (paperData) {
          const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
          const mappedQuestions = (questionsData || []).map((q: any) => {
            const optionImageWidths = q.option_image_widths || {};
            const mappedOptions = (q.options || []).map((opt: any) => ({
              ...opt,
                      imageWidth: optionImageWidths[opt.id] || opt.imageWidth || 100
            }));
            return {
              id: q.id,
              type: q.question_type || 'single_choice',
              subType: q.scratch_subtype,
              name: q.name || '',
              title: q.content,
              options: mappedOptions,
              score: q.score || 2,
              explanation: q.explanation,
              titleImages: q.title_images || [],
              titleImageWidths: q.title_image_widths || [],
              imagePosition: q.image_position || 'below',
              titleImageLayout: q.title_image_layout || 'auto',
              optionLayout: q.option_layout || 'grid'
            };
          });

          // 如果有 assignmentId，获取 enabledQuestionTypes 并过滤题目
          const assignmentId = searchParams.get('assignmentId');
          let filteredQuestions = mappedQuestions;
          let effectiveTotalScore = (paperData as any).total_score || 0;

          if (assignmentId && session) {
            try {
              const assignmentResponse = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ action: 'start_assignment', assignmentId }),
              });
              const assignmentResult = await assignmentResponse.json();
              if (assignmentResult.success && assignmentResult.data?.enabledQuestionTypes) {
                const enabledTypes = assignmentResult.data.enabledQuestionTypes;
                filteredQuestions = mappedQuestions.filter(q => enabledTypes.includes(q.type));
                effectiveTotalScore = filteredQuestions.reduce((sum, q) => sum + q.score, 0);
              }
            } catch (err) {
              console.error('获取作业题型配置失败:', err);
              // 失败时使用全部题目（向后兼容）
            }
          }

          setPaper({ ...(paperData as any), questions: filteredQuestions, total_score: effectiveTotalScore });
          setExamQuestions(filteredQuestions);
        }
      }

      setHasPermission(true);
      setLoading(false);
    };

    init();
  }, [navigate, paperId, location.state, isPreviewMode, isEditorPreview, fromResult, isProxyView]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">暂无权限</h2>
          <p className="text-gray-600 mb-6">您没有权限参加此考试，请先购买相应套餐。</p>
          <button onClick={() => navigate('/membership')} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            去购买套餐
          </button>
        </div>
      </div>
    );
  }

  // 预览模式
  if (isPreview) {
    return (
      <QuizPreviewMode
        isEditorPreview={isEditorPreview}
        previewData={location.state?.previewData}
        onBack={handleBack}
      />
    );
  }

  // 回顾模式（从结果页查看或代查模式）
  if (fromResult || isProxyView) {
    if (!paper || !reviewData) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">数据加载失败</div>
        </div>
      );
    }
    return (
      <QuizReviewMode
        paper={paper}
        examQuestions={examQuestions}
        answers={reviewData.answers}
        score={reviewData.score}
        timeLeft={reviewData.timeLeft}
        objectiveScore={reviewData.objectiveScore}
        objectiveTotalScore={reviewData.objectiveTotalScore}
        programmingScore={reviewData.programmingScore}
        questionFeedbacks={reviewData.questionFeedbacks}
        currentUser={currentUser}
        paperId={paperId}
        studentName={studentName}
        onBack={handleBack}
      />
    );
  }

  // 正常答题模式
  if (!paper || examQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">试卷加载失败</div>
      </div>
    );
  }

  return (
    <QuizAnswerMode
      paper={paper}
      examQuestions={examQuestions}
      currentUser={currentUser}
      paperId={paperId}
      isPreview={false}
      isEditorPreview={false}
      onBack={handleBack}
    />
  );
}
